import { Box, FormHelperText, Stack, TextField, useTheme } from '@mui/material';
import { TextInput } from 'components/Inputs';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { boStyle } from 'constants/content.constants';
import {
	SYSTEM_MESSAGE_CONTENT_MAX,
	SYSTEM_MESSAGE_RICH_TEXT_TOOLBAR,
	SYSTEM_MESSAGE_TITLE_MAX,
} from 'constants/system-messages.constants';
import {
	getFormInputSx,
	getMaxLengthValidationText,
	getRichTextWrapperSx,
	getValidationHelperSx,
} from '../createForm.styles';
import { CreateFormFieldLabel } from './CreateFormFieldLabel';

type CreateFormContentSectionProps = {
	titleHe: string;
	onTitleHeChange: (value: string) => void;
	titleHeTooLong: boolean;
	titleEn: string;
	onTitleEnChange: (value: string) => void;
	titleEnTooLong: boolean;
	contentHe: string;
	onContentHeChange: (value: string) => void;
	contentHeTooLong: boolean;
	contentEn: string;
	onContentEnChange: (value: string) => void;
	contentEnTooLong: boolean;
};

export const CreateFormContentSection = ({
	titleHe,
	onTitleHeChange,
	titleHeTooLong,
	titleEn,
	onTitleEnChange,
	titleEnTooLong,
	contentHe,
	onContentHeChange,
	contentHeTooLong,
	contentEn,
	onContentEnChange,
	contentEnTooLong,
}: CreateFormContentSectionProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const formInputSx = getFormInputSx(theme);
	const validationHelperSx = getValidationHelperSx();

	return (
		<Stack spacing={2}>
			<CreateFormFieldLabel label={t('page.systemMessages.field.titleAndContent')} />

			<Stack spacing={2}>
				<Stack spacing={0.75}>
					<CreateFormFieldLabel
						sub
						label={t('page.systemMessages.field.titleHe')}
						charHint={t('page.systemMessages.charHint.title')}
					/>
					<TextField
						value={titleHe}
						onChange={(event) => onTitleHeChange(event.target.value)}
						placeholder={t('page.systemMessages.field.titleHe.placeholder')}
						error={titleHeTooLong}
						helperText={titleHeTooLong ? getMaxLengthValidationText(t, SYSTEM_MESSAGE_TITLE_MAX) : undefined}
						fullWidth
						sx={formInputSx}
					/>
				</Stack>
				<Stack spacing={0.75}>
					<CreateFormFieldLabel
						sub
						label={t('page.systemMessages.field.contentHe')}
						charHint={t('page.systemMessages.charHint.content')}
					/>
					<Box sx={getRichTextWrapperSx(theme, contentHeTooLong)}>
						<TextInput
							textAlignment="right"
							inputstyle={boStyle.RICH_TEXT}
							value={contentHe}
							onChange={onContentHeChange}
							placeholder={t('page.systemMessages.field.contentHe.placeholder')}
							toolbarOptions={SYSTEM_MESSAGE_RICH_TEXT_TOOLBAR}
						/>
					</Box>
					{contentHeTooLong && (
						<FormHelperText error sx={{ ...validationHelperSx, mx: 1.75 }}>
							{getMaxLengthValidationText(t, SYSTEM_MESSAGE_CONTENT_MAX)}
						</FormHelperText>
					)}
				</Stack>
			</Stack>

			<Stack spacing={2}>
				<Stack spacing={0.75}>
					<CreateFormFieldLabel
						sub
						ltr
						label={t('page.systemMessages.field.titleEn')}
						charHint={t('page.systemMessages.charHint.title')}
					/>
					<TextField
						value={titleEn}
						onChange={(event) => onTitleEnChange(event.target.value)}
						placeholder={t('page.systemMessages.field.titleEn.placeholder')}
						error={titleEnTooLong}
						helperText={titleEnTooLong ? getMaxLengthValidationText(t, SYSTEM_MESSAGE_TITLE_MAX) : undefined}
						fullWidth
						dir="ltr"
						sx={formInputSx}
					/>
				</Stack>
				<Stack spacing={0.75}>
					<CreateFormFieldLabel
						sub
						ltr
						label={t('page.systemMessages.field.contentEn')}
						charHint={t('page.systemMessages.charHint.content')}
					/>
					<Box dir="ltr" sx={getRichTextWrapperSx(theme, contentEnTooLong)}>
						<TextInput
							inputstyle={boStyle.RICH_TEXT}
							value={contentEn}
							onChange={onContentEnChange}
							placeholder={t('page.systemMessages.field.contentEn.placeholder')}
							toolbarOptions={SYSTEM_MESSAGE_RICH_TEXT_TOOLBAR}
						/>
					</Box>
					{contentEnTooLong && (
						<FormHelperText error sx={{ ...validationHelperSx, mx: 1.75 }}>
							{getMaxLengthValidationText(t, SYSTEM_MESSAGE_CONTENT_MAX)}
						</FormHelperText>
					)}
				</Stack>
			</Stack>
		</Stack>
	);
};
